import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BE1_API } from '../../../../config/api';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { errorFromResponse } from '../../../../services/errorText';
import { formatDateTime } from '../../../../utils/formatters';
import type { DataDefinition } from '../../../../store/dataDefinitionsSlice';

/** 서버가 남긴 변경 한 칸 (data_definitions_store.diff_items) */
interface Change {
  kind: 'field' | 'columnAdded' | 'columnRemoved' | 'columnType';
  field?: string;
  name?: string;
  dataType?: string;
  before?: any;
  after?: any;
}
interface Entry { at: string; action: string; by?: string; note?: string; changes: Change[] }

/** 이미 아는 동작 — 모르는 동작(새 서버)은 코드 그대로 보인다 */
const ACTIONS = ['create', 'update', 'refresh', 'samType', 'move', 'copy', 'delete'];
/** 칸 이름 번역 키가 있는 것 — 없으면 칸 이름 그대로(source.xxx) */
const FIELDS = ['name', 'path', 'source.filepath', 'source.delimiter', 'source.hasHeader', 'source.format', 'source.encoding',
  'source.fieldWidths', 'source.fieldNames', 'source.samTypeId', 'source.connection', 'source.schema', 'source.table'];

/**
 * 데이터 정의 갱신 이력 — 언제, 누가, 무엇을 했고 **무엇이 어떻게 바뀌었는지**.
 * 레이아웃을 갱신했는데 바뀐 것이 없으면 '변경 없음' 으로 남는다(언제 확인했는지도 이력이다).
 */
const DataDefinitionHistoryDialog: React.FC<{ item: DataDefinition; onClose: () => void }> = ({ item, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [rows, setRows] = useState<Entry[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BE1_API}/api/data-definitions/items/${encodeURIComponent(item.id)}/history`);
        if (!res.ok) throw await errorFromResponse(res, res.statusText);
        const body = await res.json();
        if (alive) setRows(body.history || []);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [item.id]);

  const show = (v: any) => (v === undefined || v === null || v === '' ? t('datadef:history.empty') : String(v));
  const fieldLabel = (f?: string) => (f && FIELDS.includes(f) ? t(`datadef:history.field.${f.replace('.', '_')}`) : f || '');

  /** 만들기·복사는 '없음 → 값' 이 아니라 처음 값만 보인다 — 전부 새로 생긴 것이라 전후가 뜻이 없다 */
  const renderInitial = (c: Change, i: number) => (
    c.kind === 'field'
      ? <li key={i} className="text-gray-700"><span className="font-medium">{fieldLabel(c.field)}</span>{': '}<span className="break-all">{show(c.after)}</span></li>
      : <li key={i} className="text-gray-600">{t('datadef:history.column', { name: c.name, type: show(c.dataType) })}</li>
  );

  const renderChange = (c: Change, i: number) => {
    switch (c.kind) {
      case 'columnAdded':
        return <li key={i} className="text-green-700">{t('datadef:history.columnAdded', { name: c.name, type: show(c.dataType) })}</li>;
      case 'columnRemoved':
        return <li key={i} className="text-red-600">{t('datadef:history.columnRemoved', { name: c.name, type: show(c.dataType) })}</li>;
      case 'columnType':
        return <li key={i} className="text-amber-700">{t('datadef:history.columnType', { name: c.name, before: show(c.before), after: show(c.after) })}</li>;
      default:
        return (
          <li key={i} className="text-gray-700">
            <span className="font-medium">{fieldLabel(c.field)}</span>{': '}
            <span className="line-through text-gray-400 break-all">{show(c.before)}</span>{' → '}
            <span className="break-all">{show(c.after)}</span>
          </li>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[640px] max-w-[95vw] max-h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">{t('datadef:history.title')}</div>
            <div className="text-xs text-gray-500 truncate">{item.name}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        <div className="p-4 overflow-auto text-sm">
          {error && <div className="text-red-600 text-xs">{error}</div>}
          {!error && rows === null && <div className="text-xs text-gray-500">{t('common:ui.loading')}</div>}
          {rows && rows.length === 0 && <div className="text-xs text-gray-500">{t('datadef:history.none')}</div>}
          {rows && rows.length > 0 && (
            <ol className="space-y-3">
              {rows.map((e, i) => (
                <li key={i} className="border-l-2 border-gray-200 pl-3">
                  <div className="flex items-baseline gap-2 text-xs">
                    <span className="tabular-nums text-gray-500">{formatDateTime(e.at)}</span>
                    <span className="font-semibold text-gray-800">
                      {ACTIONS.includes(e.action) ? t(`datadef:history.action.${e.action}`) : e.action}
                    </span>
                    {e.note && <span className="text-gray-500">({e.note})</span>}
                    <span className="ml-auto text-gray-400">{e.by || '-'}</span>
                  </div>
                  {e.changes.length > 0
                    ? <ul className="mt-1 text-xs space-y-0.5">
                        {e.changes.map(e.action === 'create' || e.action === 'copy' ? renderInitial : renderChange)}
                      </ul>
                    : (e.action === 'refresh' || e.action === 'update' || e.action === 'samType') &&
                      <div className="mt-1 text-xs text-gray-400">{t('datadef:history.noChange')}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataDefinitionHistoryDialog;
